<template>
  <div>
    <v-switch v-model="fieldEnabled" inset class="mt-0">
      <template v-slot:label>
        <TextBody :text="t$('.event_has_location')" large />
      </template>
    </v-switch>
    <div class="ml-sm-14" @click="setFieldEnabled(true)">
      <AddressField
        :adh-address="adhAddress"
        :address-fetch="fetchAddresses"
        v-model="location"
        :disabled="!fieldEnabled"
      />
    </div>
  </div>
</template>

<script lang="ts">
import {
  computed,
  defineComponent,
  inject,
  ref,
  Ref,
  watch,
  WritableComputedRef,
} from "vue"
import ToggleableTextField from "features/common/forms/form-fields/ToggleableTextField.vue"
import useTranslate from "features/common/translate/concerns/useTranslate"
import EventFormService from "features/events/form/event-form.service"
import trimIfString from "features/common/util/trimIfString"
import MemberService from "features/member/member.service"
import { EventForm } from "components/EventForm/EventForm.vue"
import AddressField from "features/common/forms/form-fields/AddressField.vue"
import { addressesApi } from "store/api"
import TextBody from "components/UI/TextBody.vue"

export default defineComponent({
  name: "EventFormAddressField",
  components: { TextBody, AddressField, ToggleableTextField },
  setup() {
    const { t$ } = useTranslate("components.event_form")
    const eventFormService: EventFormService<EventForm> =
      inject<EventFormService<EventForm>>("EventFormService")
    const memberService: MemberService = inject<MemberService>("MemberService")

    eventFormService.registerProperty({
      name: "location",
    })

    const location: WritableComputedRef<string> = computed({
      get(): string {
        return eventFormService.getFormValue("location").value
      },
      set(value: string): void {
        eventFormService.setFormValue("location", trimIfString(value))
      },
    })

    const adhAddress = memberService.currentAdhAddress

    const fetchAddresses = async (query: string): Promise<string[]> => {
      const response = await addressesApi.addressSuggestions(query)
      return response.data.map((address) => address.label)
    }

    const fieldEnabledRef: Ref<boolean> = ref(!!location.value)
    watch(
      () => location.value,
      (value) => {
        fieldEnabledRef.value = !!value
      }
    )
    const fieldEnabled: WritableComputedRef<boolean> = computed({
      get: () => fieldEnabledRef.value,
      set: (value) => {
        setFieldEnabled(value)
      },
    })

    const setFieldEnabled = (value: boolean) => {
      fieldEnabledRef.value = !!value
      if (!value) {
        location.value = ""
      }
    }

    return {
      t$,
      location,
      adhAddress,
      fetchAddresses,
      fieldEnabled,
      setFieldEnabled,
    }
  },
})
</script>
